import { useChatActions, useMessageById } from '@ai-sdk-tools/store';
import { MiniIconInformation } from '@newfront-insurance/core-ui';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import type { ToolUIPart } from 'ai';
import { ArrowRight, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';

import { renderToolPendingState } from './tool-pending-state';
import {
  buildClarificationTranscript,
  getClarificationPart,
  getResolvedClarificationTranscript,
  isClarificationToolPart,
  normalizeClarificationInput,
  type ClarificationAnswer,
  type ClarificationQuestion,
} from '../../../../lib/clarification';
import type { UIMessageWithMetadata } from '../../../../lib/types';

interface ClarificationToolProps {
  part: ToolUIPart;
  messageUuid: string;
}

interface ClarificationDraftAnswer {
  optionId?: string;
  optionIds?: string[];
  skipped?: boolean;
  text?: string;
  useTextAnswer?: boolean;
}

function handleRadioKeyDown(event: KeyboardEvent<HTMLElement>, onSelect: () => void) {
  if (event.key === ' ') {
    event.preventDefault();
    onSelect();
  }
}

function getCurrentAnswer(
  question: ClarificationQuestion,
  draftAnswer: ClarificationDraftAnswer | undefined,
): ClarificationAnswer | undefined {
  if (draftAnswer?.skipped) {
    return {
      questionId: question.questionId,
      skipped: true,
    };
  }

  const trimmedText = draftAnswer?.text?.trim();

  if (question.inputType === 'multi-select') {
    const optionIds = draftAnswer?.optionIds?.filter(Boolean);

    if (optionIds?.length) {
      return {
        questionId: question.questionId,
        optionIds,
        text: trimmedText || undefined,
      };
    }
  }

  if (trimmedText) {
    return {
      questionId: question.questionId,
      text: trimmedText,
    };
  }

  if (draftAnswer?.optionId) {
    return {
      questionId: question.questionId,
      optionId: draftAnswer.optionId,
    };
  }

  return undefined;
}

export function ClarificationTool({ part, messageUuid }: ClarificationToolProps) {
  const message = useMessageById<UIMessageWithMetadata>(messageUuid);
  const { addToolResult } = useChatActions<UIMessageWithMetadata>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answersByQuestionId, setAnswersByQuestionId] = useState<Record<string, ClarificationDraftAnswer>>({});
  const [isFinalizing, setIsFinalizing] = useState(false);
  const customAnswerInputRef = useRef<HTMLInputElement>(null);
  const choiceRefs = useRef<Array<HTMLDivElement | null>>([]);

  if (!isClarificationToolPart(part)) {
    return null;
  }

  const clarificationToolPart = part;
  const questions = normalizeClarificationInput(clarificationToolPart.input).questions;

  const latestClarificationPart = getClarificationPart(message);
  const currentQuestion = questions[currentQuestionIndex];
  const currentDraftAnswer = currentQuestion ? answersByQuestionId[currentQuestion.questionId] : undefined;
  const customAnswerSelected =
    currentDraftAnswer?.useTextAnswer === true || Boolean(currentDraftAnswer?.text?.trim().length);
  const isLatestClarification = latestClarificationPart?.toolCallId === clarificationToolPart.toolCallId;
  const isOptionQuestion = currentQuestion?.inputType === 'select' || currentQuestion?.inputType === 'multi-select';
  const isMultiSelectQuestion = currentQuestion?.inputType === 'multi-select';
  const customChoiceIndex = isOptionQuestion ? (currentQuestion?.options?.length ?? -1) : -1;

  if (clarificationToolPart.state === 'input-streaming') {
    return renderToolPendingState('clarification');
  }

  if (clarificationToolPart.state === 'output-available') {
    const transcript = getResolvedClarificationTranscript(message, clarificationToolPart);

    if (!transcript) {
      return null;
    }

    return (
      <div className="my-3 ml-auto max-w-full">
        <div className="flex flex-row items-start gap-2">
          <div className="bg-muted flex max-w-full flex-col gap-4 rounded-[20px] rounded-br-[0px] px-5 py-2">
            <div className="text-steel-500 text-[14px] leading-[26px] whitespace-pre-wrap">{transcript}</div>
          </div>
        </div>
      </div>
    );
  }

  if (clarificationToolPart.state !== 'input-available' || !isLatestClarification) {
    return null;
  }

  const currentAnswer = currentQuestion ? getCurrentAnswer(currentQuestion, currentDraftAnswer) : undefined;
  const hasMessageText = message.parts.some((messagePart) => messagePart.type === 'text');

  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const allAnswersResolved = questions.every(
    (question) => !!getCurrentAnswer(question, answersByQuestionId[question.questionId]),
  );

  if (!currentQuestion) {
    return null;
  }

  const updateCurrentQuestion = (updates: ClarificationDraftAnswer) => {
    const nextAnswersByQuestionId = {
      ...answersByQuestionId,
      [currentQuestion.questionId]: {
        ...answersByQuestionId[currentQuestion.questionId],
        ...updates,
      },
    };

    setAnswersByQuestionId((previousAnswers) => ({
      ...previousAnswers,
      [currentQuestion.questionId]: {
        ...previousAnswers[currentQuestion.questionId],
        ...updates,
      },
    }));

    return nextAnswersByQuestionId;
  };

  const handleSelectOption = (optionId: string) => {
    return updateCurrentQuestion({
      optionId,
      optionIds: [],
      skipped: false,
      text: '',
      useTextAnswer: false,
    });
  };

  const handleToggleOption = (optionId: string) => {
    const existingOptionIds = currentDraftAnswer?.optionIds ?? [];
    const optionIds = existingOptionIds.includes(optionId)
      ? existingOptionIds.filter((existingOptionId) => existingOptionId !== optionId)
      : [...existingOptionIds, optionId];

    return updateCurrentQuestion({
      optionId: undefined,
      optionIds,
      skipped: false,
      text: currentDraftAnswer?.text ?? '',
      useTextAnswer: currentDraftAnswer?.useTextAnswer ?? false,
    });
  };

  const handleEnableCustomAnswer = () => {
    const preserveSelections = currentQuestion.inputType === 'multi-select';

    return updateCurrentQuestion({
      optionId: undefined,
      optionIds: preserveSelections ? (currentDraftAnswer?.optionIds ?? []) : [],
      skipped: false,
      text: currentDraftAnswer?.text ?? '',
      useTextAnswer: true,
    });
  };

  const handleTextAnswerChange = (text: string) => {
    const preserveSelections = currentQuestion.inputType === 'multi-select';

    updateCurrentQuestion({
      optionId: undefined,
      optionIds: preserveSelections ? (currentDraftAnswer?.optionIds ?? []) : [],
      skipped: false,
      text,
      useTextAnswer: true,
    });
  };

  const handleGoBack = () => {
    setCurrentQuestionIndex((index) => Math.max(0, index - 1));
  };

  const advanceOrSubmit = async (nextAnswersByQuestionId = answersByQuestionId) => {
    if (!isLastQuestion) {
      setCurrentQuestionIndex((index) => Math.min(questions.length - 1, index + 1));
      return;
    }

    const orderedAnswers = questions
      .map((question) => getCurrentAnswer(question, nextAnswersByQuestionId[question.questionId]))
      .filter((answer): answer is ClarificationAnswer => !!answer);

    if (orderedAnswers.length !== questions.length) {
      return;
    }

    setIsFinalizing(true);

    try {
      await addToolResult({
        tool: 'clarification',
        toolCallId: clarificationToolPart.toolCallId,
        output: {
          answers: orderedAnswers,
          transcript: buildClarificationTranscript(questions, orderedAnswers),
        },
      });
    } catch (error) {
      setIsFinalizing(false);
      throw error;
    }
  };

  const handleContinue = (nextAnswersByQuestionId = answersByQuestionId) => {
    const nextCurrentAnswer = getCurrentAnswer(currentQuestion, nextAnswersByQuestionId[currentQuestion.questionId]);

    if (!nextCurrentAnswer || isFinalizing) {
      return;
    }

    void advanceOrSubmit(nextAnswersByQuestionId);
  };

  const focusChoice = (choiceIndex: number) => {
    window.requestAnimationFrame(() => {
      if (choiceIndex === customChoiceIndex) {
        customAnswerInputRef.current?.focus();
        return;
      }

      choiceRefs.current[choiceIndex]?.focus();
    });
  };

  const handleSelectChoiceByIndex = (choiceIndex: number) => {
    if (!currentQuestion.options) {
      return answersByQuestionId;
    }

    if (choiceIndex === customChoiceIndex) {
      return handleEnableCustomAnswer();
    }

    const optionId = currentQuestion.options[choiceIndex]?.optionId;
    if (!optionId) {
      return answersByQuestionId;
    }

    return currentQuestion.inputType === 'multi-select' ? handleToggleOption(optionId) : handleSelectOption(optionId);
  };

  const moveChoiceSelection = (choiceIndex: number, direction: -1 | 1) => {
    if (!currentQuestion.options || isFinalizing) {
      return;
    }

    const nextChoiceIndex = Math.min(customChoiceIndex, Math.max(0, choiceIndex + direction));
    if (nextChoiceIndex === choiceIndex) {
      return;
    }

    if (!isMultiSelectQuestion) {
      handleSelectChoiceByIndex(nextChoiceIndex);
    }
    focusChoice(nextChoiceIndex);
  };

  const handleChoiceKeyDown = (event: KeyboardEvent<HTMLDivElement>, choiceIndex: number) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveChoiceSelection(choiceIndex, 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveChoiceSelection(choiceIndex, -1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      const nextAnswersByQuestionId = handleSelectChoiceByIndex(choiceIndex);
      if (choiceIndex === customChoiceIndex) {
        const customText = nextAnswersByQuestionId[currentQuestion.questionId]?.text?.trim();

        if (!customText) {
          focusChoice(choiceIndex);
          return;
        }
      }

      if (isMultiSelectQuestion && choiceIndex !== customChoiceIndex) {
        return;
      }

      handleContinue(nextAnswersByQuestionId);
      return;
    }

    handleRadioKeyDown(event, () => {
      handleSelectChoiceByIndex(choiceIndex);
    });
  };

  const handleCustomAnswerInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveChoiceSelection(customChoiceIndex, -1);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveChoiceSelection(customChoiceIndex, 1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleContinue();
    }
  };

  const handleTextQuestionKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleContinue();
    }
  };

  const handleSkipQuestion = () => {
    if (isFinalizing) {
      return;
    }

    const nextAnswersByQuestionId = {
      ...answersByQuestionId,
      [currentQuestion.questionId]: {
        optionId: undefined,
        optionIds: [],
        skipped: true,
        text: '',
        useTextAnswer: false,
      },
    };

    setAnswersByQuestionId(nextAnswersByQuestionId);
    void advanceOrSubmit(nextAnswersByQuestionId);
  };

  const continueDisabled = isFinalizing || !currentAnswer || (isLastQuestion && !allAnswersResolved);
  const introCopy =
    questions.length === 1
      ? 'For best results let me ask you a follow up question:'
      : 'For best results let me ask you a few follow up questions:';

  return (
    <div className="mt-3 flex w-full flex-col gap-3">
      {!hasMessageText && <p className="text-steel-500 text-[14px] leading-[26px]">{introCopy}</p>}

      <div className="bg-aqua-citron-gradient w-full rounded-[16px] p-px">
        <div className="bg-aqua-50 flex w-full flex-col rounded-[15px]">
          <div className="flex items-start justify-between gap-6 px-[30px] py-5">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="text-steel-500 text-[14px] leading-5 font-semibold">{currentQuestion.question}</p>
              {currentQuestion.helperText && (
                <p className="text-steel-400 text-[12px] leading-4">{currentQuestion.helperText}</p>
              )}
            </div>

            <div className="flex shrink-0 flex-col items-center gap-[5px]">
              <div className="flex items-center gap-1">
                {questions.map((question, index) => {
                  const resolvedAnswer = getCurrentAnswer(question, answersByQuestionId[question.questionId]);

                  return (
                    <span
                      key={question.questionId}
                      aria-hidden="true"
                      className={cn('h-2 w-2 rounded-full transition-colors', {
                        'bg-steel-400': index === currentQuestionIndex,
                        'bg-brand-400': index !== currentQuestionIndex && resolvedAnswer,
                        'bg-steel-75': index !== currentQuestionIndex && !resolvedAnswer,
                      })}
                    />
                  );
                })}
              </div>

              <div className="text-steel-400 flex items-center gap-1 text-[12px] leading-4">
                <button
                  type="button"
                  className="text-steel-400 flex size-[18px] items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={handleGoBack}
                  disabled={currentQuestionIndex === 0 || isFinalizing}
                  aria-label="Previous clarification question"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span>{`${currentQuestionIndex + 1}/${questions.length}`}</span>
                <button
                  type="button"
                  className="text-steel-400 flex size-[18px] items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => handleContinue()}
                  disabled={isLastQuestion || !currentAnswer || isFinalizing}
                  aria-label="Next clarification question"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-steel-75 border-t border-solid px-[15px] pt-[10px]">
            <div className="flex flex-col gap-[3px]">
              {isOptionQuestion && currentQuestion.options && (
                <div
                  role={isMultiSelectQuestion ? 'group' : 'radiogroup'}
                  aria-label={currentQuestion.question}
                  className="flex flex-col gap-[3px]"
                >
                  {currentQuestion.options.map((option, optionIndex) => {
                    const optionId = option.optionId;
                    const isSelected = Boolean(optionId)
                      ? isMultiSelectQuestion
                        ? (currentDraftAnswer?.optionIds?.includes(optionId) ?? false)
                        : currentDraftAnswer?.optionId === optionId
                      : false;
                    const canSelectOption = !isFinalizing && Boolean(optionId);

                    return (
                      <div
                        key={option.optionId ?? `${currentQuestion.questionId}-option-${optionIndex}`}
                        ref={(element) => {
                          choiceRefs.current[optionIndex] = element;
                        }}
                        role={isMultiSelectQuestion ? 'checkbox' : 'radio'}
                        aria-checked={isSelected}
                        tabIndex={canSelectOption ? 0 : -1}
                        className={cn(
                          'group flex w-full items-center justify-between gap-3 rounded-[20px] px-4 py-[10px] text-left transition-colors duration-150',
                          canSelectOption && 'cursor-pointer',
                          isSelected ? 'bg-aqua-200' : 'hover:bg-aqua-100',
                          !canSelectOption && 'cursor-not-allowed opacity-60',
                        )}
                        onClick={() => {
                          if (optionId) {
                            if (isMultiSelectQuestion) {
                              handleToggleOption(optionId);
                              return;
                            }

                            handleSelectOption(optionId);
                          }
                        }}
                        onKeyDown={(event) => handleChoiceKeyDown(event, optionIndex)}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div
                            className={cn(
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center border border-solid',
                              isMultiSelectQuestion ? 'rounded-[4px]' : 'rounded-full',
                              isSelected ? 'border-brand-400 bg-brand-400' : 'border-steel-200',
                            )}
                          >
                            {isMultiSelectQuestion ? (
                              isSelected ? (
                                <Check className="size-3 text-white" />
                              ) : null
                            ) : (
                              <div
                                className={cn('h-2 w-2 rounded-full', {
                                  'bg-brand-400': isSelected,
                                })}
                              />
                            )}
                          </div>
                          <p className={cn('text-[14px] leading-5', isSelected ? 'text-steel-500' : 'text-steel-400')}>
                            {option.label}
                          </p>
                        </div>

                        {option.description && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="text-steel-200 group-hover:text-steel-300 flex size-5 shrink-0 items-center justify-center transition-colors"
                                  onClick={(event) => event.stopPropagation()}
                                  aria-label={`More information about ${option.label}`}
                                >
                                  <MiniIconInformation color="currentColor" size={16} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="left"
                                align="center"
                                sideOffset={8}
                                className="bg-steel-500 max-w-[368px] rounded-[6px] border-0 px-2 py-1 text-[12px] leading-4 whitespace-normal text-white shadow-none"
                              >
                                {option.description}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    );
                  })}

                  <div
                    ref={(element) => {
                      if (customChoiceIndex >= 0) {
                        choiceRefs.current[customChoiceIndex] = element;
                      }
                    }}
                    role={isMultiSelectQuestion ? 'checkbox' : 'radio'}
                    aria-checked={customAnswerSelected}
                    tabIndex={isFinalizing ? -1 : 0}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-[20px] px-4 py-[10px] text-left transition-colors duration-150',
                      !isFinalizing && 'cursor-pointer',
                      customAnswerSelected ? 'bg-aqua-200' : 'hover:bg-aqua-100',
                      isFinalizing && 'cursor-not-allowed opacity-60',
                    )}
                    onClick={() => {
                      if (customAnswerSelected) {
                        customAnswerInputRef.current?.focus();
                        return;
                      }

                      handleEnableCustomAnswer();
                      focusChoice(customChoiceIndex);
                    }}
                    onKeyDown={(event) => handleChoiceKeyDown(event, customChoiceIndex)}
                  >
                    <div
                      className={cn(
                        'flex h-[18px] w-[18px] shrink-0 items-center justify-center border border-solid',
                        isMultiSelectQuestion ? 'rounded-[4px]' : 'rounded-full',
                        customAnswerSelected ? 'border-brand-400 bg-brand-400' : 'border-steel-200',
                      )}
                    >
                      {isMultiSelectQuestion ? (
                        customAnswerSelected ? (
                          <Check className="size-3 text-white" />
                        ) : null
                      ) : (
                        <div
                          className={cn('h-2 w-2 rounded-full', {
                            'bg-brand-400': customAnswerSelected,
                          })}
                        />
                      )}
                    </div>
                    {customAnswerSelected ? (
                      <input
                        ref={customAnswerInputRef}
                        value={currentDraftAnswer?.text ?? ''}
                        onChange={(event) => handleTextAnswerChange(event.target.value)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => {
                          event.stopPropagation();
                          handleCustomAnswerInputKeyDown(event);
                        }}
                        placeholder="Start typing..."
                        aria-label={`${currentQuestion.question} custom answer`}
                        className="text-steel-500 placeholder:text-steel-300 min-w-0 flex-1 bg-transparent text-[14px] leading-5 outline-none"
                        disabled={isFinalizing}
                      />
                    ) : (
                      <p className="text-steel-400 text-[14px] leading-5">Something else</p>
                    )}
                  </div>
                </div>
              )}

              {currentQuestion.inputType === 'text' && (
                <textarea
                  value={currentDraftAnswer?.text ?? ''}
                  onChange={(event) => handleTextAnswerChange(event.target.value)}
                  onKeyDown={handleTextQuestionKeyDown}
                  placeholder="Start typing..."
                  className={cn(
                    'border-steel-200 min-h-[120px] w-full resize-none rounded-[8px] border border-solid bg-white',
                    'text-steel-500 placeholder:text-steel-300 px-3 py-3 text-[14px] leading-5',
                    'focus:border-brand-400 outline-none',
                  )}
                  disabled={isFinalizing}
                />
              )}
            </div>
          </div>

          <div className="flex items-center justify-between px-[27px] py-5">
            <button
              type="button"
              className="text-brand-400 text-[14px] leading-5 font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleSkipQuestion}
              disabled={isFinalizing}
            >
              Skip question
            </button>

            <button
              type="button"
              className={cn(
                'text-moss-500 inline-flex items-center gap-[10px] rounded-full px-4 py-2 text-[14px] leading-5 font-semibold transition-opacity',
                'bg-lime-blue-gradient',
                continueDisabled && 'cursor-not-allowed opacity-50',
              )}
              onClick={() => handleContinue()}
              disabled={continueDisabled}
            >
              <ArrowRight className="size-[18px]" />
              <span>Continue</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
